import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { Account, AccountState, AccountFilters } from '../types'
import AccountServiceClass from '../services/accountService'

// Initial state for account management
const initialState: AccountState = {
  accounts: [],
  loading: false,
  error: null,
  lastFetched: null,
}

// Async thunk for fetching accessible accounts
export const fetchAccessibleAccounts = createAsyncThunk(
  'account/fetchAccessibleAccounts',
  async (params?: { useCache?: boolean }) => {
    const service = AccountServiceClass.getInstance()
    const useCache = params?.useCache !== false // Default to true
    return service.getAccessibleAccounts(useCache)
  }
)

// Async thunk for fetching filtered accounts
export const fetchFilteredAccounts = createAsyncThunk(
  'account/fetchFilteredAccounts', 
  async (params: { filters: AccountFilters; useCache?: boolean }) => {
    const service = AccountServiceClass.getInstance()
    const useCache = params.useCache !== false // Default to true
    return service.getFilteredAccounts(params.filters, useCache)
  }
)

// Async thunk for refreshing accounts (bypasses cache)
export const refreshAccounts = createAsyncThunk(
  'account/refreshAccounts',
  async () => {
    const service = AccountServiceClass.getInstance()
    return service.refreshAccounts()
  }
)

// Async thunk for getting a single account by ID
export const fetchAccountById = createAsyncThunk(
  'account/fetchAccountById',
  async (params: { accountId: string; useCache?: boolean }) => {
    const service = AccountServiceClass.getInstance()
    const useCache = params.useCache !== false // Default to true
    return service.getAccountById(params.accountId, useCache)
  }
)

// Account slice
const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    // Clear any errors
    clearError: (state) => {
      state.error = null
    },
    
    // Clear all accounts (useful for logout)
    clearAccounts: (state) => {
      state.accounts = []
      state.lastFetched = null
      state.error = null
    },
    
    // Update a specific account (useful for real-time updates)
    updateAccount: (state, action: PayloadAction<Account>) => {
      const index = state.accounts.findIndex(account => account.id === action.payload.id)
      if (index !== -1) {
        state.accounts[index] = action.payload
      }
    },
    
    // Set accounts directly (useful for optimistic updates)
    setAccounts: (state, action: PayloadAction<Account[]>) => {
      state.accounts = action.payload
      state.lastFetched = Date.now()
    },
    
    // Mark an account as active/inactive
    setAccountActive: (state, action: PayloadAction<{ accountId: string; isActive: boolean }>) => {
      const account = state.accounts.find(acc => acc.id === action.payload.accountId)
      if (account) {
        account.isActive = action.payload.isActive
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch accessible accounts
    builder
      .addCase(fetchAccessibleAccounts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAccessibleAccounts.fulfilled, (state, action) => {
        state.loading = false
        state.accounts = action.payload
        state.lastFetched = Date.now()
        state.error = null
      })
      .addCase(fetchAccessibleAccounts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch accounts'
      })

    // Fetch filtered accounts  
    builder
      .addCase(fetchFilteredAccounts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFilteredAccounts.fulfilled, (state, action) => {
        state.loading = false
        state.accounts = action.payload
        state.lastFetched = Date.now()
        state.error = null
      })
      .addCase(fetchFilteredAccounts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch filtered accounts'
      })

    // Refresh accounts
    builder
      .addCase(refreshAccounts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshAccounts.fulfilled, (state, action) => {
        state.loading = false
        state.accounts = action.payload
        state.lastFetched = Date.now()
        state.error = null
      })
      .addCase(refreshAccounts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to refresh accounts'
      })

    // Fetch account by ID (doesn't replace the full list)
    builder
      .addCase(fetchAccountById.fulfilled, (state, action) => {
        if (action.payload) {
          // Update the account if it exists in the list, otherwise add it
          const existingIndex = state.accounts.findIndex(acc => acc.id === action.payload!.id)
          if (existingIndex !== -1) {
            state.accounts[existingIndex] = action.payload
          } else {
            state.accounts.push(action.payload)
          }
        }
      })
      .addCase(fetchAccountById.rejected, (state, action) => {
        // Don't change loading state for single account fetch
        state.error = action.error.message || 'Failed to fetch account'
      })
  },
})

// Export actions
export const { 
  clearError, 
  clearAccounts, 
  updateAccount, 
  setAccounts, 
  setAccountActive 
} = accountSlice.actions

// Export reducer
export default accountSlice.reducer

// Selectors for easy access to account state (compatible with RootState)
export const selectAccounts = (state: { Account: AccountState }) => state.Account.accounts
export const selectAccountsLoading = (state: { Account: AccountState }) => state.Account.loading
export const selectAccountsError = (state: { Account: AccountState }) => state.Account.error
export const selectAccountsLastFetched = (state: { Account: AccountState }) => state.Account.lastFetched

// Derived selectors
export const selectActiveAccounts = (state: { Account: AccountState }) => 
  state.Account.accounts.filter(account => account.isActive !== false)

export const selectAccountsByMarketplace = (marketplace: string) => (state: { Account: AccountState }) =>
  state.Account.accounts.filter(account => account.marketplace === marketplace)

export const selectAccountById = (accountId: string) => (state: { Account: AccountState }) =>
  state.Account.accounts.find(account => account.id === accountId)

// Check if accounts data is stale (older than 30 minutes)
export const selectIsAccountsDataStale = (state: { Account: AccountState }) => {
  if (!state.Account.lastFetched) return true
  const now = Date.now()
  const STALE_THRESHOLD = 30 * 60 * 1000 // 30 minutes
  return (now - state.Account.lastFetched) > STALE_THRESHOLD
}